import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, Link, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import UserSearch from '@components/UserSearch'
import ToggleListItem from '@components/ToggleListItem'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useCreateSubjectConversation from './hooks/useCreateSubjectConversation'

import ExistingConversation from './components/ExistingConversation'

const MODAL_KEY = 'CreateSubjectConversationModal'

type AfterActionAndCloseModalParams = {
  afterAction: () => void,
  closeModal: () => void,
}

const afterActionAndCloseModal = (params: AfterActionAndCloseModalParams) => {
  const { afterAction, closeModal } = params

  if (afterAction) afterAction()

  closeModal()
}

export type ModalCallbacks = {
  closeModal: () => void,
  createSubjectConversation: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  actionText: string,
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateSubjectConversationModal = (props: ModalProps) => {
  const {
    actionText,
    callbacks,
    callbacks: {
      closeModal,
      createSubjectConversation,
    },
    showModal,
  } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    callbacks: {
      afterAction,
    },
    conversation: initConversation,
    conversation: {
      campaign_id,
    },
    defaultMessage,
  } = modalPayload

  const createSubjectConversationPayload = useCreateSubjectConversation({
    initConversation,
    defaultMessage,
    requestOptions: {
      include_comment_user: true,
      include_conversation_comments: true,
      include_conversation_subscribers: true,
      include_image_sizes: true,
      include_subscriber_user: true,
      include_user_image: true,
    },
  })

  const {
    callbacks: {
      buildActionParams,
      createCommentWithConversation: createFn,
      toggleUser,
    },
    commentForm: {
      entityState,
      entityState: {
        body,
        isPublic,
      },
      setEntityState,
    },
    conversation: {
      id: conversationId,
    },
    creating,
    filteredSubscribers,
    filteredSubscribersCount,
    hasFilteredComments,
    hasFilteredSubscribers,
    hasSelectedUsers,
    lastComment,
    saveEnabled,
    selectedUsers,
  } = createSubjectConversationPayload

  const createActionPayload = {
    callbacks: {
      action: createFn,
      afterAction: () => afterActionAndCloseModal({ afterAction, closeModal }),
      buildActionParams,
    },
    entityParams: entityState,
    toastText: 'Sent for Approval',
  }

  return (
    <SidebarModal callbacks={callbacks} modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header
        callbacks={callbacks}
        title={conversationId ? 'Add To' : 'Start'}
        titleSecondLine="Conversation"
      />

      <SidebarModal.Content>
        <Form>
          {hasFilteredSubscribers && (
            <ExistingConversation
              filteredSubscribers={filteredSubscribers}
              filteredSubscribersCount={filteredSubscribersCount}
              hasFilteredComments={hasFilteredComments}
              // @ts-expect-error TS2322
              lastComment={lastComment}
            />
          )}

          <Form.Field
            direction="column"
            label="Add Users"
            marginTop={hasFilteredSubscribers ? 'large' : 0}
          >
            <UserSearch
              // @ts-expect-error TS2741
              callbacks={{
                selectItem: user => toggleUser(user),
              }}
              endpoint={`/campaigns/${campaign_id}/users.json`}
              // @ts-expect-error TS2339
              selectedIds={selectedUsers.map(user => user.id)}
            />
          </Form.Field>

          {hasSelectedUsers && (
            <Form.Field direction="column" label="Selected Recipients" marginTop="large">
              {selectedUsers.map((selectedUser) => {
                const { email, full_name, image } = selectedUser

                return (
                  <SidebarModal.User
                    disableHover
                    image={image}
                    // @ts-expect-error TS2322
                    imageKey="thumb_url"
                    key={email}
                    subtitle={email}
                    title={full_name}
                  >
                    <Link
                      greyLink
                      onClick={() => toggleUser(selectedUser)}
                      textProps={{ fontSize: 'small' }}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </Link>
                  </SidebarModal.User>
                )
              })}
            </Form.Field>
          )}

          {(hasFilteredSubscribers || hasSelectedUsers) && (
            <>
              <Form.Field direction="column" label="Message Body" marginTop="large">
                <textarea
                  name="body"
                  onChange={e => setEntityState({ body: e.target.value })}
                  placeholder="Add a Message (Optional)"
                  style={{ height: 80 }}
                  value={body}
                />
              </Form.Field>

              <Form.Field direction="column" label="Privacy" marginTop="large">
                <ToggleListItem
                  onClick={() => setEntityState({ isPublic: !isPublic })}
                  toggleValue={isPublic}
                >
                  <ToggleListItem.Title>
                    {isPublic ? 'Public Comment' : 'Private Comment (Agency Only)'}
                  </ToggleListItem.Title>
                </ToggleListItem>
              </Form.Field>
            </>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          // @ts-expect-error TS2345
          onClick={() => createSubjectConversation(createActionPayload)}
          size="large"
        >
          {saveEnabled ? actionText || 'Start Conversation' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateSubjectConversationModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
